import { css } from "@emotion/react";
import {
    Children,
    Fragment,
    isValidElement,
    type PropsWithChildren,
    type ReactElement,
    useCallback,
    useState,
} from "react";
import { Accordion, Badge } from "reshaped";

import Await from "../async/Await";
import { getSpaceVariable } from "../space/utilities";
import Text from "../Text";
import { useTheme } from "../theme/hooks";
import { StepperContext, type StepperContextType } from "./constants";
import type { StepProps } from "./Step";
import Step from "./Step";

type StepperProps = PropsWithChildren;

function isStepElement(child: unknown): child is ReactElement<StepProps> {
    return isValidElement(child) && child.type === Step;
}

export default function Stepper(props: StepperProps) {
    const steps = Children.toArray(props.children).filter(isStepElement);
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

    const lastStepIndex = steps.length - 1;
    const canGoBack = currentStepIndex > 0;
    const canGoForward = currentStepIndex < lastStepIndex;
    const prev = useCallback(() => {
        setCurrentStepIndex((index) => Math.max(0, index - 1));
    }, []);
    const next = useCallback(() => {
        setCurrentStepIndex((index) => Math.min(lastStepIndex, index + 1));
    }, [lastStepIndex]);

    return steps.map((step, i) => (
        <Fragment key={step.props.label}>
            <StepView
                number={i + 1}
                label={step.props.label}
                active={i === currentStepIndex}
                canGoBack={canGoBack}
                canGoForward={canGoForward}
                onPrev={prev}
                onNext={next}
            >
                {step.props.children}
            </StepView>
        </Fragment>
    ));
}

type StepViewProps = PropsWithChildren<{
    number: number;
    label: string;
    active: boolean;
    canGoBack: boolean;
    canGoForward: boolean;
    onPrev: () => void;
    onNext: () => void;
}>;
function StepView(props: StepViewProps) {
    const { number, label, active, canGoBack, canGoForward, onPrev, onNext } = props;
    const theme = useTheme();

    const containerCss = css({
        display: "flex",
        alignItems: "center",
        gap: getSpaceVariable(4),
    });

    const contentCss = css({
        borderLeft: `1px solid ${theme.divider}`,
        padding: getSpaceVariable(4),
        marginTop: getSpaceVariable(4),
        marginBottom: getSpaceVariable(4),
        marginLeft: getSpaceVariable(4),
    });

    const contextValue: StepperContextType = {
        step: number,
        canGoBack,
        canGoForward,
        goBack: onPrev,
        goForward: onNext,
    };

    return (
        <Accordion active={active}>
            <div css={containerCss}>
                <Badge color={active ? "primary" : "neutral"}>Etape {number}</Badge>
                <div>
                    <Text variant="body">{label}</Text>
                </div>
            </div>
            <div css={contentCss}>
                <Accordion.Content>
                    {active ? (
                        <Await>
                            <StepperContext.Provider value={contextValue}>
                                {props.children}
                            </StepperContext.Provider>
                        </Await>
                    ) : null}
                </Accordion.Content>
            </div>
        </Accordion>
    );
}
